import type { PolymarketTrader } from "@/lib/polymarket";

/** Parse market categories from various backend formats (JSON array, comma-separated string, or array) */
export function parseCategories(raw: any): string[] {
  let cats: string[] = [];
  if (!raw) return cats;
  if (Array.isArray(raw)) {
    cats = raw;
  } else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) cats = parsed;
      else return cats;
    } catch {
      cats = raw.split(",").map((s: string) => s.trim()).filter(Boolean);
    }
  }
  // Deduplicate categories (case-insensitive, keep first occurrence)
  const seen = new Set<string>();
  return cats.filter((c) => {
    const key = c.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Format a number as compact USD string ($1.2M, $45K, $123) */
export function formatUsd(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(0)}K`;
  return `$${abs.toFixed(0)}`;
}

/** Normalize trader data from backend response to PolymarketTrader */
export function normalizeTrader(trader: any): PolymarketTrader {
  let categories: string[] | undefined;
  if (Array.isArray(trader.marketCategories)) {
    categories = trader.marketCategories;
  } else if (typeof trader.marketCategories === "string" && trader.marketCategories) {
    categories = trader.marketCategories.split(",").map((s: string) => s.trim());
  }

  return {
    address: trader.address || "",
    username: trader.username || undefined,
    avatar: trader.avatar || undefined,
    bio: trader.bio || undefined,
    totalVolume: Number(trader.totalVolume) || 0,
    totalProfit: Number(trader.totalProfit) || 0,
    totalLoss: Number(trader.totalLoss) || 0,
    totalPnL: Number(trader.totalPnL) || 0,
    currentBalance: Number(trader.currentBalance) || 0,
    totalPositions: Number(trader.totalPositions) || 0,
    activePositions: Number(trader.activePositions) || 0,
    wins: Number(trader.wins) || 0,
    losses: Number(trader.losses) || 0,
    winRate: Number(trader.winRate) || 0,
    positionsAboveEntry: Number(trader.positionsAboveEntry) || undefined,
    rank: Number(trader.rank) || undefined,
    joinedDate: trader.joinedDate || undefined,
    marketCategories: categories,
    tradeCount: Number(trader.tradeCount) || 0,
    marketsCount: Number(trader.marketsCount) || 0,
    largestWin: Number(trader.largestWin) || 0,
    followers: Number(trader.followers) || 0,
    copierCount: Number(trader.copierCount) || 0,
    joinedAt: trader.joinedAt || null,
    primaryStyle: trader.primaryStyle || undefined,
    profit7d: Number(trader.profit7d) || 0,
    profit30d: Number(trader.profit30d) || 0,
    roi: trader.roi ?? null,
    entryEdge: trader.entryEdge ?? null,
    profitFactor: trader.profitFactor ?? null,
    maxDrawdownPct: trader.maxDrawdownPct ?? null,
    avgBuyPrice: trader.avgBuyPrice ?? null,
    avgSellPrice: trader.avgSellPrice ?? null,
    avgTradeSize: trader.avgTradeSize ?? null,
    isFrenFlowUser: trader.isFrenFlowUser || false,
    frenFlowUsername: trader.frenFlowUsername || undefined,
    frenFlowName: trader.frenFlowName || undefined,
    frenFlowAvatar: trader.frenFlowAvatar || undefined,
    xUsername: trader.xUsername || trader.x_username || null,
    // 2026-05-18: HFT-bot flag from backend's enrichTradersWithHftFlag.
    // Without passing this through, the red badge in UsersGrid never
    // renders even when the API returns isHft:true.
    isHft: trader.isHft === true,
    tradesPerHour: typeof trader.tradesPerHour === 'number' ? trader.tradesPerHour : undefined,
  };
}
